import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import Button from "@mui/material/Button";
import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card } from "../general/card";
import useBackend from "../../hooks/use_backend";
import { useParams } from "react-router-dom";
import { startOfDay } from "date-fns";
import { format } from "date-fns";
import { fetchSiteProduct } from "../../scripts/api_v2/site_client";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import config from "../../scripts/config";

const COLUMN_LABELS = new Map([
  ["consumption", "Verbruik (kW)"],
  ["production", "Productie (kW)"],
  ["flex_charge", "Batterij opladen (kW)"],
  ["flex_discharge", "Batterij ontladen (kW)"],
  ["curtailed_production", "Uitgeschakelde productie (kW)"],
  ["uncurtailed_production", "Effectieve productie (kW)"],
  ["grid", "Net (kW)"],
  ["grid_offtake", "Net afname (kW)"],
  ["grid_inject", "Net injectie (kW)"],
  ["soc", "State of charge (%)"],
  ["imb_price", "Onbalans prijs (€/MWh)"],
  ["epex", "Epex prijs (€/MWh)"],
  ["forecast", "Forecast (kWh)"],
  ["nominated", "Nominated (kWh)"],
  ["actual", "Actual (kWh)"],
  ["credit_increment", "Flexibility compensation increment (€)"],
]);

const ALL_COLUMNS = Array.from(COLUMN_LABELS.keys());

export default function Export({ startDate, endDate }) {
  const theme = useTheme();
  const backend = useBackend();
  const params = useParams();

  const { data: product, isLoading: isLoadingProduct } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_PRODUCT, backend.fetch, params.site_id],
    queryFn: () => fetchSiteProduct(backend.fetch, params.site_id),
  });

  const isEpexSteering = product?.conf_optimizer === "EPEX";

  // Combine all loading states that affect which columns are available.
  // Add future query loading states here as they are introduced.
  const isLoadingColumns = isLoadingProduct;

  // credit_increment is not applicable for EPEX-steered sites
  const availableColumns = React.useMemo(
    () => (isEpexSteering ? ALL_COLUMNS.filter((col) => col !== "credit_increment") : ALL_COLUMNS),
    [isEpexSteering],
  );

  const [selectedExportColumns, setSelectedExportColumns] = useState([...ALL_COLUMNS]);

  // useMutation is used instead of useQuery because this is a one-shot imperative action (a file download),
  // not data that needs to be cached or displayed. isPending replaces manual loading state.
  const { mutate: exportData, isPending: isDownloadingExport } = useMutation({
    mutationFn: async () => {
      // The export API expects a [start, end) range in Unix seconds.
      // We always start at midnight of the selected start day...
      const start = startOfDay(new Date(startDate));
      // ...and end at midnight of the day *after* the selected end day, so the full end day is included.
      const nextDay = new Date(endDate);
      nextDay.setDate(nextDay.getDate() + 1);
      const end = startOfDay(nextDay);

      // Convert to Unix timestamps (seconds) as required by the API.
      const fromts = Math.floor(start.valueOf() / 1000);
      const tots = Math.floor(end.valueOf() / 1000);

      // Always include utctime as the first column so the export has a timestamp reference.
      const columnsParam = ["utctime", ...selectedExportColumns].join(",");
      const url = `${config.REACT_APP_HOST}/api/v2/metering/export/${params.site_id}?fromts=${fromts}&tots=${tots}&columns=${columnsParam}`;

      const response = await backend.fetch(url);

      // response.blob() reads the raw binary response body (the Excel file bytes) into memory as a Blob.
      // A Blob is a browser object that represents raw binary data — in this case the .xlsx file content.
      // We need it as a Blob because the browser cannot directly "save" a fetch response;
      // we have to create a temporary in-memory object URL from it to trigger a download.
      const blob = await response.blob();

      // The backend sets Content-Disposition: attachment; filename="export-xxx.xlsx" to suggest a filename.
      // We try to read that first, and fall back to a timestamp-based name if it's missing.
      const contentDisposition = response.headers.get("content-disposition");
      const filename =
        contentDisposition?.split("filename=")[1] || `export-${format(new Date(), "yyyy-MM-dd-HH-mm")}.xlsx`;

      // Create a temporary URL pointing to the Blob in memory so the browser can reference it as a file.
      const objectUrl = window.URL.createObjectURL(blob);

      // Programmatically create a hidden <a> tag and click it to trigger the browser's file download dialog.
      // This is the standard pattern for downloading files from fetch responses in the browser.
      const link = document.createElement("a");
      link.href = objectUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Release the object URL to free the memory held by the Blob.
      // Without this the browser would keep the file bytes in memory until the page is closed.
      window.URL.revokeObjectURL(objectUrl);
    },
    onError: (e) => console.error(e),
  });

  function isLargeExportRange() {
    const msInDay = 1000 * 60 * 60 * 24;
    const diffInMs = Math.abs(new Date(endDate).getTime() - new Date(startDate).getTime());
    return diffInMs > 31 * msInDay;
  }

  return (
    <Card
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      title="Export"
      sx={{ minHeight: 200 }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem", width: "max-content", maxWidth: "100%" }}>
        {isLargeExportRange() && (
          <Typography fontSize={12} color="text.secondary">
            Large data range, export may take a while
          </Typography>
        )}
        <FormControl size="small" fullWidth>
          <InputLabel id="export-columns-label" style={{ color: theme.palette.text.primary }}>
            Select columns
          </InputLabel>
          <Select
            labelId="export-columns-label"
            disabled={isDownloadingExport || isLoadingColumns}
            multiple
            value={selectedExportColumns.filter((col) => availableColumns.includes(col))}
            onChange={(event) => setSelectedExportColumns(event.target.value)}
            input={<OutlinedInput label="Select columns" />}
            renderValue={(selected) =>
              isLoadingColumns
                ? "Loading..."
                : selected
                    .filter((col) => availableColumns.includes(col))
                    .map((value) => COLUMN_LABELS.get(value))
                    .join(", ")
            }
          >
            {availableColumns.map((column) => (
              <MenuItem key={column} value={column}>
                <Checkbox checked={selectedExportColumns.indexOf(column) > -1} />
                <ListItemText primary={COLUMN_LABELS.get(column)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          size="small"
          sx={{ height: "30px", boxShadow: "none", textTransform: "none" }}
          fullWidth
          disabled={isDownloadingExport || !params.site_id}
          onClick={exportData}
        >
          {isDownloadingExport && <CircularProgress size={15} />}
          {!isDownloadingExport && (
            <Typography color="white" fontWeight={600}>
              Download
            </Typography>
          )}
        </Button>
      </Box>
    </Card>
  );
}
